import ApiFetch, { DelegateFetch } from "@maxis/client-logic/apis/apiFetch";
import useLoadingIndicator from "@maxis/client-logic/hooks/useLoadingIndicator";
import { register } from "@maxis/client-logic/utils/serviceWorkerRegistration";
import { getLocalStorageItem } from "@maxis/client-logic/utils/localStorageUtil";
import { LOCAL_STORAGE_KEYS } from "@maxis/client-logic/constants/enumerates";
import { completeServerUrl, parseDotnetAPIUrl } from "@maxis/client-logic/utils/urlUtil";
import DotnetApiFetch from "@maxis/client-logic/apis/dotnetApiFetch";
import "react-toastify/dist/ReactToastify.css";
import { isEmpty, omit } from "lodash";

import useMessageBox from "@maxis/client-logic/hooks/useMessageBox";
import { useToast } from "@maxis/client-logic/hooks/useToast";
import { mutationKey, runDeduped } from "@maxis/client-logic/utils/mutationDedupe";
import { READ_LOADER_DELAY_MS, deferLoader, isReadRequest } from "@maxis/client-logic/utils/loaderDelay";

export default function useFetch() {
  const { confirm, alert, error } = useMessageBox();
  const { showLoading, hideLoading } = useLoadingIndicator();
  const { Loading, FinishLoading, CloseToast } = useToast();

  const backendType = import.meta.env.VITE_BACKEND_TYPE; // JAVA or DOTNET
  const API_SERVER_URL =
    completeServerUrl(import.meta.env.VITE_API_SERVER_URL) + "/api";

  const defaultMessageOptions = {
    showConfirm: false,
    showLoad: true,
    confirmMessage: "진행하시겠습니까?",
    showSuccess: false,
    showAlert: true,
    alertTitle: "",
  };

  const _callRequest = async (url, options, messageOptions) => {
    let hasError = false;
    const loadingId = messageOptions.reqType || "LOADING";
    // Reads show the loader only if they outlast READ_LOADER_DELAY_MS; writes show it at
    // once, because its overlay is what blocks a second click (see loaderDelay.js).
    const loader =
      messageOptions.showLoad === true
        ? deferLoader(
            () => Loading(loadingId),
            isReadRequest(url, options, messageOptions) ? READ_LOADER_DELAY_MS : 0
          )
        : null;
    const finishRequestLoading = () => {
      if (!messageOptions.showLoad) return;
      loader?.cancel();
      if (loader && !loader.shown) return;
      if (messageOptions.reqType) FinishLoading(loadingId);
      else CloseToast(loadingId);
    };
    try {
      let response;
      if (backendType === "DOTNET") {
        let endpoint = "";
        if (url === "/login") endpoint = "Login";
        if (url === "/user/authentication") endpoint = "Authentication";
        if (url === "/menuLog") endpoint = "MenuLog";
        if (url === "/logout") {
          finishRequestLoading();
          return;
        }
        // V4 URL null 체크
        if (url && url.indexOf("file-") > 0) endpoint = url;

        // url 패턴은 /"시스템구분"/"프로그램ID"/"메소드명" 으로 되어야 함
        let programId, methodName;
        if (url === null) {
          endpoint = "Execute";
          programId = options.isSystem ? "SYSTEM" : options.serviceId;
          methodName = Object.keys(options.process[0])[0];
          methodName = methodName[0].toUpperCase() + methodName.slice(1);
          options.body = omit(options, ["serviceId", "process"]);
        }

        if (!endpoint) {
          let parsedUrl = parseDotnetAPIUrl(url);
          programId = parsedUrl.programId;
          methodName = parsedUrl.methodName;
          methodName = methodName.charAt(0).toUpperCase() + methodName.slice(1);
          if (programId && methodName) {
            endpoint = "Execute";
          }
        }

        if (!endpoint) {
          throw new Error(
            ".NET API 호출을 위한 필수파라미터가 잘못되었습니다."
          );
        }
        response = await DotnetApiFetch.request({
          programId: programId || "",
          methodName: methodName || "",
          options: options || {},
          endpoint: endpoint,
        });
      }

      if (backendType === "JAVA") {
        if (!isEmpty(url)) {
          response = await ApiFetch.request(url, options);
        } else {
          // console.log("options=", options);
          response = await DelegateFetch.tran(options);
        }
      }

      if (messageOptions.showSuccess) {
        alert("완료", response?.responseText);
      }
      if (
        response &&
        Array.isArray(response) &&
        (response.length < 1 ||
          (response.length === 1 &&
            typeof response[0] === "object" &&
            Object.keys(response[0]).length === 0)) &&
        (messageOptions.showLoad ?? true)
      ) {
        finishRequestLoading();
      } else if (!response && (messageOptions.showLoad ?? true)) {
        finishRequestLoading();
      } else {
        if (messageOptions.showLoad ?? true) {
          finishRequestLoading();
        }
      }
      // FinishLoading();
      return response;
    } catch (e) {
      if (e.name !== "multiValidation" && messageOptions.showAlert) {
        error(messageOptions.alertTitle || "확인", e.message);
        hasError = true;
        // Must pass loadingId. Loading() created this toast with toastId = reqType
        // ("SAVE" for saveRequest, "DEL" for deleteRequest), but CloseToast() with no
        // argument only dismisses the five hard-coded ids ERROR/WARNING/SUCCESS/INFO/
        // LOADING. So a failed save left "저장 중입니다..." spinning forever while the
        // error dialog had already been dismissed.
        if (!loader || loader.shown) CloseToast(loadingId);
        return;
      }
      throw e;
    } finally {
      // A delayed loader must not fire after the request is over, on any exit path --
      // otherwise a fast failure would leave the overlay up with nothing to close it.
      loader?.cancel();
    }
  };

  const ocrRequest = async (options) => {
    let messageOptions = {
      showConfirm: true,
      confirmMessage: "저장하시겠습니까?",
      showSuccess: true,
      showAlert: true,
      alertTitle: "확인",
    };
    messageOptions = { ...defaultMessageOptions, ...messageOptions };
    showLoading();
    try {
      const baseUrl =
        completeServerUrl(import.meta.env.VITE_API_SERVER_URL) + "/api";
      if (messageOptions.showConfirm) {
        const confirmResult = await confirm(
          "확인",
          messageOptions.confirmMessage
        );
        if (confirmResult) {
          const accessToken = getLocalStorageItem(
            LOCAL_STORAGE_KEYS.ACCESS_TOKEN
          );
          const formData = new FormData();
          for (let file of options.attachFiles) {
            formData.append("file", file);
          }
          let res = await fetch(baseUrl + "/ocr/file-upload", {
            headers: { Authorization: `Bearer ${accessToken}` },
            method: "POST",
            body: formData,
          });
          const result = await res.json();

          if (result.responseCode == "0000") {
            //일단 첫번째꺼만 들어온다 추후 배열 뷰 개발 예정
            const ATTACH_SEQ = result.data.ATTACH_SEQ[0];
            return ATTACH_SEQ;
          } else {
            if (messageOptions.showAlert) {
              error(messageOptions.alertTitle ?? "확인");
            }
          }
        }
      }
    } catch (e) {
      if (messageOptions.showAlert) {
        error(messageOptions.alertTitle ?? "확인", e.message);
      }
      return null;
    } finally {
      hideLoading();
    }
  };

  /**
   * _callRequest, but an identical mutation already in flight is not sent again -- the
   * caller awaits the first one and gets its result, so code that checks the response
   * (`if (response && response.KEY_ID)`) keeps working.
   *
   * Keyed per request, not globally, because flows like SYS140 fire two writes in a row
   * (InsertUser then InserUserAuth); a global lock would have the first block the second.
   */
  const _callRequestOnce = (url, options, messageOptions) =>
    runDeduped(mutationKey(url, options), () =>
      _callRequest(url, options, messageOptions)
    );

  const request = async (url, options = {}, messageOptions) => {
    messageOptions = { ...defaultMessageOptions, ...messageOptions };
    try {
      if (messageOptions.showConfirm) {
        const confirmResult = await confirm(
          "확인",
          messageOptions.confirmMessage
        );
        if (confirmResult) {
          const response = await _callRequestOnce(url, options, messageOptions);
          return response;
        }
      } else {
        const response = await _callRequestOnce(url, options, messageOptions);
        return response;
      }
    } catch (e) {
      FinishLoading(messageOptions.reqType, "error");
      // opt-in: a caller that reports failures itself needs the reason, not undefined
      if (messageOptions.rethrow) throw e;
    }
  };

  const saveRequest = async (
    url,
    options,
    messageOptions,
    showConMessage = true
  ) => {
    // console.log(
    //   "url:  ",
    //   url,
    //   " ,  options:  ",
    //   options,
    //   "  , messageOptions: ",
    //   messageOptions
    // );
    return await request(url, options, {
      showConfirm: showConMessage,
      confirmMessage: "저장하시겠습니까?",
      showSuccess: false,
      showAlert: true,
      alertTitle: "확인",
      reqType: "SAVE",
      ...messageOptions,
    });
  };

  const deleteRequest = async (url, options, messageOptions) => {
    return await request(url, options, {
      showConfirm: true,
      confirmMessage: "삭제하시겠습니까?",
      showSuccess: false,
      showAlert: false,
      alertTitle: "확인",
      reqType: "DEL",
      ...messageOptions,
    });
  };

  const downloadFile = async (attachFileSeq, saveInDB = false) => {
    const param = {
      ATTACH_FILE_SEQ: attachFileSeq,
      SAVE_IN_DB: saveInDB,
    };

    if (backendType === "DOTNET") {
      const endpoint = "/file-info";
      const options = {
        method: "POST",
        body: param,
      };

      let response = await DotnetApiFetch.request({
        programId: "",
        methodName: "",
        options: options || {},
        endpoint: endpoint,
      });

      if (response) {
        const filename = response[0]?.SAVE_FILE_NM;

        if (filename) {
          const downloadFilename = filename;
          const url = `${
            import.meta.env.VITE_API_SERVER_FILE_URL
          }/Download/${encodeURI(downloadFilename)}`;
          let a = document.createElement("a");
          a.href = url;
          a.click();
        }
      }
    } else if (backendType === "JAVA") {
      const response = await request("/saveDownloadToken", {
        body: param,
      });

      const db = response.SAVE_IN_DB == false ? "0" : "1";
      var fileUrl =
        completeServerUrl(import.meta.env.VITE_API_SERVER_URL) +
        `/api/file-download/${encodeURIComponent(
          response.DOWNLOAD_TOKEN
        )}?db=${db}`; // 파일 다운로드를 처리하는 서버의 엔드포인트로 변경해야 합니다.
      let a = document.createElement("a");
      a.href = fileUrl;
      a.click();
      // if (response) {
      //   const linkSource = `data:${response.FILE_TYPE};base64,${response.FILE_CONTENT}`;
      //   let a = document.createElement('a');
      //   //a.href = response.FILE_CONTENT;
      //   a.href = linkSource;
      //   a.download = response.FILE_NAME;
      //   a.click();
      // }
    }
  };

  const downloadFileBase64 = async (attachFileSeq, saveInDB = false) => {
    const param = {
      ATTACH_FILE_SEQ: attachFileSeq,
      SAVE_IN_DB: saveInDB,
    };
    const response = await request("/saveDownloadToken", {
      body: param,
    });

    const db = response.SAVE_IN_DB === "false" ? "0" : "1";

    const fileResponse = await request("/file-download-base64", {
      body: { downloadToken: response.DOWNLOAD_TOKEN, db: db },
    });
    return fileResponse;
  };

  const partialDownloadFile = async (attachFileSeq, saveInDB = false) => {
    const param = {
      ATTACH_FILE_SEQ: attachFileSeq,
      SAVE_IN_DB: saveInDB,
    };
    const response = await request("/saveDownloadToken", {
      body: param,
    });

    return request(
      `/file-partial-download/${encodeURIComponent(response.DOWNLOAD_TOKEN)}`,
      {
        method: "GET",
        fileToken: "insighton.co.kr",
      }
    );
  };
  register();

  const subscribeToSSE = async (url, clientId) => {
    const accessToken = getLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);

    // const eventSource = await ApiFetch.request(url, {
    //   method:"POST",
    //   body: {clientId: clientId}
    // });

    const eventSource = new request(API_SERVER_URL + url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "POST",
      body: { clientId: clientId },
    });

    eventSource.addEventListener("EVN_INIT", (event) => {
      // console.log("초기설정 메시지:", event.data);
    });

    eventSource.addEventListener("message", (event) => {
      // console.log("서버 측 메시지:", event.data);
    });

    eventSource.onerror = (error) => {
      // console.error("SSE 에러:", error);
      eventSource.close();
    };

    return eventSource;
  };

  return {
    ocrRequest,
    _callRequest,
    request,
    saveRequest,
    deleteRequest,
    downloadFile,
    downloadFileBase64,
    partialDownloadFile,
    subscribeToSSE,
  };
}
